import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SHIPMENT_URLS } from '../config/routeUrls';
import {
  createShipment,
  charterShipment,
  modifyShipmentWithCarrierNotice,
  cancelCarrier,
  cancelClient,
  deleteClientCarrier,
  requestCMR,
  deleteShipment,
  restoreShipment,
} from '../services/api';
import { extractErrorMessage, createErrorMessageBoxConfig } from '../utils/errorHandler';
import { trim_dataset } from '../utils/dataFormatting';
import { useForm } from '../components/BaseForm/FormContext';

export const useShipmentActions = (
  formData,
  setFormData,
  setMessageBoxConfig,
  setShowMessageBox,
  updateField,
  updateForm,
  validateFields,
  shipmentNumber,
  isNew
) => {
  const navigate = useNavigate();
  const { handleConfirmBack, setHasChanged } = useForm();

  const navigateToEnCours = useCallback(() => {
    navigate(SHIPMENT_URLS.EN_COURS);
  }, [navigate]);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleCreateShipment = useCallback(() => {
    if (validateFields(['client', 'dates', 'addresses'])) {
      const updatedData = { ...formData };
      handleSubmit(updatedData);
    }
  }, [formData, validateFields]);

  const handleModifyShipment = useCallback(() => {
    const fieldsToValidate = [
      'client',
      formData.shipment_stage !== 'A TRAITER' && 'carrier',
      'dates',
      'addresses',
    ];
    if (validateFields(fieldsToValidate)) {
      const updatedData = { ...formData };
      handleSubmit(updatedData);
    }
  }, [formData, validateFields]);

  const handleSubmit = useCallback(
    async updatedData => {
      try {
        updatedData = trim_dataset(updatedData);
        const response = await updateForm(updatedData);

        if (response.status === 201) {
          setHasChanged(false);
          setMessageBoxConfig({
            message: `Le dossier ${response.data.shipment_number} a bien été créé.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setShowMessageBox(false);
              navigate(SHIPMENT_URLS.A_TRAITER);
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        } else if (response.status === 200) {
          setHasChanged(false);
          setMessageBoxConfig({
            message: `Le dossier ${response.data.shipment_number} a bien été mis à jour.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du dossier :', extractErrorMessage(error));
        const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
        setMessageBoxConfig({
          ...errorConfig,
          message: `Erreur lors de la sauvegarde du dossier: ${extractErrorMessage(error)}`,
        });
        setShowMessageBox(true);
      }
    },
    [updateForm, setMessageBoxConfig, setShowMessageBox]
  );

  const handleCharterShipment = useCallback(
    e => {
      if (e) e.preventDefault();

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        setMessageBoxConfig({
          message: `Affréter le ${isNew ? 'nouveau' : ''} dossier ${
            !isNew ? shipmentNumber : ''
          } et envoyer l'ordre de transport à l'adresse ${
            formData.carrier_email
          } ? Les modifications les plus récentes seront enregistrées avant l'envoi.`,
          type: 'yesno',
          loading_button: true,
          input: false,
          onConfirm: handleConfirmChartering,
          onCancel: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    },
    [
      formData.carrier,
      formData.carrier_email,
      isNew,
      shipmentNumber,
      validateFields,
      setMessageBoxConfig,
      setShowMessageBox,
    ]
  );

  const handleConfirmChartering = useCallback(async () => {
    const CreateShipment = async () => {
      try {
        const newShipmentData = await createShipment(formDataRef.current);
        return newShipmentData;
      } catch (error) {
        throw error;
      }
    };

    const CharterShipment = async (data = null) => {
      try {
        const response = await charterShipment(data || formDataRef.current);
        return response;
      } catch (error) {
        throw error;
      }
    };

    const buildCharterMessage = (shipmentNum, carrierCode, carrierEmail) => {
      const base = shipmentNum
        ? `Le dossier ${shipmentNum} a bien été affrété.`
        : `Le dossier a bien été affrété.`;
      return `${base}\nTransporteur: ${carrierCode}\nEmail: ${carrierEmail}`;
    };

    try {
      if (isNew) {
        const newShipmentData = await CreateShipment();
        if (newShipmentData.status === 201) {
          const updatedFormData = {
            ...formDataRef.current,
            ...newShipmentData.data,
          };
          setFormData(updatedFormData);

          const charterResponse = await CharterShipment(updatedFormData);
          if (charterResponse.status === 200) {
            setHasChanged(false);
            setMessageBoxConfig({
              message: buildCharterMessage(
                newShipmentData.data.shipment_number,
                updatedFormData.carrier,
                updatedFormData.carrier_email
              ),
              type: 'ok',
              loading_button: false,
              input: false,
              onConfirm: () => {
                setShowMessageBox(false);
                navigate(SHIPMENT_URLS.EN_COURS);
              },
              onCancel: () => setShowMessageBox(false),
              onClose: () => setShowMessageBox(false),
            });
            setShowMessageBox(true);
          }
        }
      } else {
        const charterResponse = await CharterShipment();
        if (charterResponse.status === 200) {
          setMessageBoxConfig({
            message: buildCharterMessage(
              shipmentNumber,
              formDataRef.current.carrier,
              formDataRef.current.carrier_email
            ),
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setShowMessageBox(false);
              navigateToEnCours();
            },
            onCancel: () => setShowMessageBox(false),
            onClose: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      }
    } catch (error) {
      const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
      setMessageBoxConfig({
        ...errorConfig,
        message: `Erreur lors de l'affrètement: ${extractErrorMessage(error)}`,
      });
      setShowMessageBox(true);
    }
  }, [isNew, setFormData, setMessageBoxConfig, setShowMessageBox, navigateToEnCours]);

  const handleModifyShipmentWithCarrierNotice = useCallback(
    e => {
      if (e) e.preventDefault();

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        setMessageBoxConfig({
          message: `Modifier le dossier ${shipmentNumber} et avertir le transporteur ${formData.carrier} à l'adresse ${formData.carrier_email} ?`,
          type: 'yesno',
          loading_button: true,
          input: false,
          onConfirm: handleConfirmModifyShipmentWithCarrierNotice,
          onCancel: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    },
    [
      formData.carrier,
      formData.carrier_email,
      shipmentNumber,
      validateFields,
      setMessageBoxConfig,
      setShowMessageBox,
    ]
  );

  const handleConfirmModifyShipmentWithCarrierNotice = useCallback(async () => {
    try {
      const response = await modifyShipmentWithCarrierNotice(formDataRef.current);
      if (response.status === 200) {
        setMessageBoxConfig({
          message: `Le transporteur a bien été notifié des modifications.\nTransporteur: ${formDataRef.current.carrier}\nEmail: ${formDataRef.current.carrier_email}`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => {
            setShowMessageBox(false);
            navigateToEnCours();
          },
          onCancel: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    } catch (error) {
      const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
      setMessageBoxConfig({
        ...errorConfig,
        message: `Erreur lors de la notification: ${extractErrorMessage(error)}`,
      });
      setShowMessageBox(true);
    }
  }, [setMessageBoxConfig, setShowMessageBox, navigateToEnCours]);

  const handleCancelCarrier = useCallback(
    e => {
      if (e) e.preventDefault();

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        setMessageBoxConfig({
          message: `Annuler le dossier ${shipmentNumber} et avertir le transporteur ${formData.carrier} à l'adresse ${formData.carrier_email} ? Les modifications les plus récentes seront enregistrées avant l'envoi.`,
          type: 'yesno',
          loading_button: true,
          input: false,
          onConfirm: handleConfirmCancelCarrier,
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    },
    [
      formData.carrier,
      formData.carrier_email,
      shipmentNumber,
      validateFields,
      setMessageBoxConfig,
      setShowMessageBox,
    ]
  );

  const handleConfirmCancelCarrier = useCallback(async () => {
    try {
      const response = await cancelCarrier(formDataRef.current);
      if (response.status === 200) {
        setHasChanged(false);
        setMessageBoxConfig({
          message: `Le transporteur a bien été annulé.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => {
            setShowMessageBox(false);
            navigate(SHIPMENT_URLS.A_TRAITER);
          },
          onCancel: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    } catch (error) {
      const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
      setMessageBoxConfig({
        ...errorConfig,
        message: `Erreur lors de l'annulation: ${extractErrorMessage(error)}`,
      });
      setShowMessageBox(true);
    }
  }, [setMessageBoxConfig, setShowMessageBox, navigate, setHasChanged]);

  const handleCancelClient = useCallback(
    e => {
      if (e) e.preventDefault();

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        setMessageBoxConfig({
          message: `Annuler le dossier ${shipmentNumber} côté client ? Le prix client sera mis à 0. Les modifications les plus récentes seront enregistrées.`,
          type: 'yesno',
          loading_button: true,
          input: false,
          onConfirm: handleConfirmCancelClient,
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    },
    [shipmentNumber, validateFields, setMessageBoxConfig, setShowMessageBox]
  );

  const handleConfirmCancelClient = useCallback(async () => {
    try {
      const response = await cancelClient(formDataRef.current);
      if (response.status === 200) {
        setMessageBoxConfig({
          message: `Le dossier a bien été annulé côté client.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onCancel: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    } catch (error) {
      const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
      setMessageBoxConfig({
        ...errorConfig,
        message: `Erreur lors de l'annulation client: ${extractErrorMessage(error)}`,
      });
      setShowMessageBox(true);
    }
  }, [setMessageBoxConfig, setShowMessageBox]);

  const handleDeleteClientCarrier = useCallback(
    e => {
      if (e) e.preventDefault();

      if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
        setMessageBoxConfig({
          message: `Merci d'indiquer le motif de l'annulation. Le transporteur ${formData.carrier} sera averti à l'adresse ${formData.carrier_email}.  Les modifications les plus récentes seront enregistrées avant l'envoi.`,
          type: 'input',
          loading_button: true,
          input: true,
          onConfirm: motif => handleConfirmDeleteClientCarrier(motif),
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    },
    [
      formData.carrier,
      formData.carrier_email,
      validateFields,
      setMessageBoxConfig,
      setShowMessageBox,
    ]
  );

  const handleConfirmDeleteClientCarrier = useCallback(
    async motif => {
      try {
        let updatedFormData = {
          ...formDataRef.current,
          cancellation_reason: motif,
        };

        const response = await deleteClientCarrier(updatedFormData);
        if (response.status === 200) {
          setHasChanged(false);
          setMessageBoxConfig({
            message: `Le dossier ${shipmentNumber} a bien été annulé au client et au transporteur, le transporteur en a été averti.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setShowMessageBox(false);
              handleConfirmBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } catch (error) {
        const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
        setMessageBoxConfig({
          ...errorConfig,
          message: `Une erreur est survenue lors de l'annulation tps - cli du dossier: ${extractErrorMessage(
            error
          )}`,
        });
        setShowMessageBox(true);
      }
    },
    [shipmentNumber, setMessageBoxConfig, setShowMessageBox]
  );

  const handleDeleteShipment = useCallback(
    async e => {
      if (e) e.preventDefault();

      setMessageBoxConfig({
        message: `Etes vous certain.e de vouloir supprimer le dossier ${shipmentNumber} ? Il pourra être restauré depuis la page dédiée.`,
        type: 'yesno',
        loading_button: true,
        input: false,
        onConfirm: async () => {
          try {
            const response = await deleteShipment(shipmentNumber);
            if (response.status === 200) {
              setHasChanged(false);
              setMessageBoxConfig({
                message: `Le dossier ${shipmentNumber} a été supprimé.`,
                type: 'ok',
                loading_button: false,
                input: false,
                onConfirm: () => {
                  setShowMessageBox(false);
                  handleConfirmBack();
                },
                onCancel: () => setShowMessageBox(false),
              });
              setShowMessageBox(true);
            }
          } catch (error) {
            const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
            setMessageBoxConfig({
              ...errorConfig,
              message: `Erreur lors de la suppression: ${extractErrorMessage(error)}`,
            });
            setShowMessageBox(true);
          }
        },
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    },
    [shipmentNumber, setMessageBoxConfig, setShowMessageBox]
  );

  const handleRestoreShipment = useCallback(() => {
    setMessageBoxConfig({
      message: `Etes vous certain.e de vouloir restaurer le dossier ${shipmentNumber} ? Il apparaitra à nouveau dans les dossiers ${formData.shipment_stage}.`,
      type: 'yesno',
      loading_button: true,
      input: false,
      onConfirm: async () => {
        try {
          const response = await restoreShipment(shipmentNumber);
          if (response.status === 200) {
            setHasChanged(false);
            setMessageBoxConfig({
              message: `Le dossier ${shipmentNumber} a été restauré. Il est à nouveau disponible dans les dossiers ${response.data.shipment_stage}.`,
              type: 'ok',
              loading_button: false,
              input: false,
              onConfirm: () => {
                setShowMessageBox(false);
                handleConfirmBack();
              },
              onCancel: () => setShowMessageBox(false),
            });
            setShowMessageBox(true);
          }
        } catch (error) {
          const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
          setMessageBoxConfig({
            ...errorConfig,
            message: `Erreur lors de la restauration: ${extractErrorMessage(error)}`,
          });
          setShowMessageBox(true);
        }
      },
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  }, [shipmentNumber, formData.shipment_stage, setMessageBoxConfig, setShowMessageBox]);

  const handleRequestCMR = useCallback(async () => {
    try {
      const response = await requestCMR(formData);
      if (response.status === 200) {
        setMessageBoxConfig({
          message: `La demande de CMR a bien été envoyée à l'adresse ${formData.carrier_email}.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onClose: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    } catch (error) {
      const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
      setMessageBoxConfig({
        ...errorConfig,
        message: `Une erreur est survenue lors de la demande de CMR: ${extractErrorMessage(error)}`,
      });
      setShowMessageBox(true);
    }
  }, [formData, setMessageBoxConfig, setShowMessageBox]);

  const handleRequestCMRClick = useCallback(() => {
    if (formData.carrier_email) {
      setMessageBoxConfig({
        message: `Envoyer une demande de CMR à l'adresse mail ${formData.carrier_email} ?`,
        type: 'yesno',
        loading_button: true,
        input: false,
        onConfirm: handleRequestCMR,
        onClose: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    } else {
      setMessageBoxConfig({
        message: `Aucune adresse mail n'est renseignée dans le champ email transporteur.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onClose: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  }, [formData.carrier_email, handleRequestCMR, setMessageBoxConfig, setShowMessageBox]);

  return {
    handleCreateShipment,
    handleModifyShipment,
    handleSubmit,
    handleCharterShipment,
    handleConfirmChartering,
    handleModifyShipmentWithCarrierNotice,
    handleConfirmModifyShipmentWithCarrierNotice,
    handleCancelCarrier,
    handleConfirmCancelCarrier,
    handleCancelClient,
    handleConfirmCancelClient,
    handleDeleteClientCarrier,
    handleConfirmDeleteClientCarrier,
    handleDeleteShipment,
    handleRestoreShipment,
    handleRequestCMR,
    handleRequestCMRClick,
  };
};
